import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Zap, Shield } from "lucide-react";
import heroImage from "@/assets/hero-nightlife.jpg";
import krewLogo from "@/assets/krew-logo.png";

const stats = [
  { icon: Users, value: "Zero IOUs", label: "No more fronting" },
  { icon: Zap, value: "Under 5 min", label: "To plan a night" },
  { icon: Shield, value: "Locked In", label: "Deposits = attendance" },
];

const HeroSection = () => {
  return (
    <section className="relative min-h-screen overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt="Friends enjoying nightlife"
          className="h-full w-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/70 to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-transparent to-background/60" />
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-6 pt-24 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-2xl"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-6 flex items-center gap-3"
          >
            <img src={krewLogo} alt="Krew" className="h-12 w-12 drop-shadow-lg" />
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5">
              <span className="h-2 w-2 animate-pulse-glow rounded-full bg-primary" />
              <span className="text-xs font-medium text-primary">
                The Social Nightlife OS
              </span>
            </div>
          </motion.div>

          <h1 className="mb-6 text-5xl font-bold leading-[1.1] tracking-tight md:text-7xl">
            <span className="text-gradient-hero">Nightlife,</span>
            <br />
            <span className="text-gradient-gold">solved for groups.</span>
          </h1>

          <p className="mb-10 max-w-lg text-lg font-body text-muted-foreground leading-relaxed">
            No more "who pays?" drama. No more 47-message group chats. Create a Krew, 
            lock commitment, auto-split — and just enjoy the night.
          </p>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <Link
              to="/auth"
              className="group inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-8 py-4 text-base font-semibold text-primary-foreground transition-all glow-neon hover:scale-[1.02]"
            >
              Start Your Krew
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="#how-it-works"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-border px-8 py-4 text-base font-medium text-foreground transition-all hover:border-primary/50 hover:bg-muted"
            >
              See How It Works
            </a>
          </div>
        </motion.div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-20 grid max-w-xl grid-cols-3 gap-6"
        >
          {stats.map((stat) => (
            <div key={stat.label} className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <stat.icon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xl font-bold text-foreground">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
